import type { ReactElement } from "react";
import { Compass, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AuthBackdrop } from "@/components/layout/AuthBackdrop";
import { Button } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalTitle,
} from "@/components/ui/modal";

type WelcomeTourDialogProps = {
  open: boolean;
  onStart: () => void;
  onSkip: () => void;
};

export function WelcomeTourDialog({ open, onStart, onSkip }: WelcomeTourDialogProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onSkip();
      }}
    >
      <ModalContent
        className="overflow-hidden sm:max-w-md lg:overflow-hidden"
        mobileFullscreen={false}
        showCloseButton={false}
        containInDrawer={false}
      >
        <AuthBackdrop size="card" />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute top-3 right-3 z-10"
          onClick={onSkip}
        >
          <XIcon className="size-4" aria-hidden />
          <span className="sr-only">{t("productTour.skip")}</span>
        </Button>
        <ModalBody className="relative z-10 flex flex-col items-center gap-3 pt-8 text-center sm:pt-8">
          <div
            className="flex size-12 items-center justify-center rounded-2xl bg-primary/10"
            aria-hidden
          >
            <Compass className="size-5 text-primary" />
          </div>
          <div className="flex flex-col gap-1">
            <ModalTitle className="text-center">{t("productTour.welcomeTitle")}</ModalTitle>
            <ModalDescription className="text-center">
              {t("productTour.welcomeDescription")}
            </ModalDescription>
          </div>
        </ModalBody>
        <ModalFooter className="relative z-10 justify-between bg-background/80 sm:justify-between">
          <Button type="button" variant="outline" onClick={onSkip}>
            {t("productTour.skip")}
          </Button>
          <Button type="button" onClick={onStart}>
            {t("productTour.start")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
